import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { map, Observable } from 'rxjs'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { OptionsCalendarParamsInterface } from '@modules/options-module/general/interfaces/options-calendar-params.interface'
import { OptionsResponseInterface } from '@modules/options-module/general/interfaces/options-response.interface'
import { OptionsParamsInterface } from '@modules/options-module/general/interfaces/options-params.interface'
import { OptionsExistsParamsInterface } from '@modules/options-module/general/interfaces/options-exists-params.Interface'
import { OptionsExistsResponseInterface } from '@modules/options-module/general/interfaces/options-exists-response.Interface'
import { OptionContractDetailsParamsInterface } from '@modules/options-module/general/interfaces/option-contract-details-params.Interface'
import { OptionContractDetailsInterface } from '@modules/options-module/general/interfaces/option-contract-details.interface'

@Injectable({
	providedIn: 'root',
})
export class OptionsService {
	protected readonly string: StringConstructor = String
	protected readonly object: ObjectConstructor = Object

	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)

	public getCalendar(calendarParams: OptionsCalendarParamsInterface): Observable<string[]> {
		let params: HttpParams = new HttpParams()

		this.object.entries(calendarParams).forEach(([key, value]: [string, unknown]): void => {
			params = params.append(key, this.string(value))
		})

		return this._http
			.get<string[]>(`${this._environmentsHelperService.apiUrl}/options/v1/options_calendar/`, { params })
			.pipe(
				map((response: string[]): string[] =>
					response.sort((a: string, b: string): number => new Date(a).getTime() - new Date(b).getTime()),
				),
			)
	}

	public getOptions(optionsParams: OptionsParamsInterface): Observable<OptionsResponseInterface> {
		let params: HttpParams = new HttpParams()

		this.object.entries(optionsParams).forEach(([key, value]: [string, unknown]): void => {
			params = params.append(key, this.string(value))
		})

		return this._http.get<OptionsResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/options/v1/options_polygon_list/`,
			{ params },
		)
	}

	public getOptionsByNextUrl(url: string): Observable<OptionsResponseInterface> {
		const params: HttpParams = new HttpParams().append('url', url)

		return this._http.get<OptionsResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/proxy_api/v1/polygon/next_url/`,
			{ params },
		)
	}

	public getOptionsExists(
		optionsExistsParams: OptionsExistsParamsInterface,
	): Observable<OptionsExistsResponseInterface> {
		let params: HttpParams = new HttpParams()

		this.object.entries(optionsExistsParams).forEach(([key, value]: [string, unknown]): void => {
			params = params.append(key, this.string(value))
		})

		return this._http.get<OptionsExistsResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/options/v1/options_exists/`,
			{
				params,
			},
		)
	}

	public getOptionContractDetails(
		optionContractDetailsParams: OptionContractDetailsParamsInterface,
	): Observable<OptionContractDetailsInterface> {
		let params: HttpParams = new HttpParams()

		this.object.entries(optionContractDetailsParams).forEach(([key, value]: [string, unknown]): void => {
			params = params.append(key, this.string(value))
		})

		return this._http.get<OptionContractDetailsInterface>(
			`${this._environmentsHelperService.apiUrl}/options/v1/options_contract/`,
			{
				params,
			},
		)
	}
}
